import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge as NeonEdgeAtom } from '@sorare/core/src/atoms/ui/NeonEdge';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';

import { RevealPackCardsDialogFromReward } from 'components/commonCard/RevealPackCardsDialogFromReward';
import { useSeriesDailyClaim } from 'hooks/packs/useSeriesDailyClaim';

import type { SeriesDailyClaimBlock_task } from './__generated__/index.graphql';

const Wrapper = styled.div`
  padding: 0;
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  background: var(--c-nl-500);
  width: 100%;
`;

const NeonEdge = styled(NeonEdgeAtom)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--double-unit);
  width: 100%;
`;

const Pack = styled(Vertical)`
  max-height: 100px;
  max-width: 110px;
  align-items: flex-end;
  justify-content: flex-end;
  margin-bottom: calc(-1 * var(--quadruple-unit));
`;

const PackImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ButtonWrapper = styled.div`
  display: block;
  border-radius: var(--unit);
  background: var(--c-nd-50-opaque);
`;
const NotificationDot = styled.div`
  position: absolute;
  top: var(--unit);
  right: var(--double-unit);
`;

type Props = {
  task?: SeriesDailyClaimBlock_task;
  refetch: () => Promise<unknown>;
};

export const SeriesDailyClaimBlock = ({ task, refetch }: Props) => {
  const {
    isClaimable,
    pack,
    onClaim,
    reward,
    packDialogOpen,
    setPackDialogOpen,
    claimMutationData,
    dailyTasksExpiresAt,
  } = useSeriesDailyClaim(task);

  return (
    <>
      <Wrapper className={classNames({ disabled: !isClaimable })}>
        <NeonEdge color="#00A2F3" showNeon="always">
          {!!isClaimable && (
            <NotificationDot>
              <LiveDot animate size="md" />
            </NotificationDot>
          )}
          {!!pack?.pictureUrl && (
            <Pack>
              <PackImage
                src={proxyUrl(pack.pictureUrl, { cropWidth: 160 })}
                alt=""
              />
            </Pack>
          )}
          <ButtonWrapper>
            <Button
              color="primary"
              size="large"
              onClick={onClaim}
              disabled={!isClaimable}
            >
              {isClaimable ? (
                <FormattedMessage
                  id="FreePackButton.freeClaim"
                  defaultMessage="Free claim"
                />
              ) : (
                dailyTasksExpiresAt && (
                  <TimeLeft time={dailyTasksExpiresAt} onEnded={refetch} />
                )
              )}
            </Button>
          </ButtonWrapper>
        </NeonEdge>
      </Wrapper>
      {!!reward && (
        <RevealPackCardsDialogFromReward
          open={packDialogOpen}
          reward={claimMutationData?.rewards?.at(0)}
          onClose={() => {
            setPackDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

SeriesDailyClaimBlock.fragments = {
  task: gql`
    fragment SeriesDailyClaimBlock_task on Task {
      id
      ...useSeriesDailyClaim_task
    }
    ${useSeriesDailyClaim.fragments.task}
  ` as TypedDocumentNode<SeriesDailyClaimBlock_task>,
};
